import { useEffect } from 'react';
import { translate } from '../../i18n/translations';
import { useLocaleStore } from '../../stores/localeStore';

export interface SeoMetadata {
  title: string;
  description: string;
  canonicalPath: string;
  image?: string;
  robots?: string;
  structuredData?: Record<string, unknown>;
}

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element!.setAttribute(name, value));
};

const localizeMetadata = (value: string, locale: ReturnType<typeof useLocaleStore.getState>['locale']) => {
  const direct = translate(value, locale);
  if (direct !== value) return direct;

  const titled = value.match(/^(.*) \| mapbl$/);
  if (titled) {
    const localized = translate(titled[1], locale);
    if (localized !== titled[1]) return `${localized} | mapbl`;
  }

  const paged = value.match(/^(.*) – page (\d+)( \| mapbl)?$/);
  if (paged) {
    const localized = translate(paged[1], locale);
    if (localized !== paged[1]) return `${localized} – ${translate('Page', locale).toLocaleLowerCase(locale)} ${paged[2]}${paged[3] || ''}`;
  }

  return value;
};

export const useSeoHead = ({
  title,
  description,
  canonicalPath,
  image = '/og.png',
  robots = 'index,follow,max-image-preview:large',
  structuredData,
}: SeoMetadata) => {
  const locale = useLocaleStore((state) => state.locale);
  const localizedTitle = localizeMetadata(title, locale);
  const localizedDescription = localizeMetadata(description, locale);

  useEffect(() => {
    const origin = window.location.origin;
    const canonical = new URL(canonicalPath, origin).toString();
    const imageUrl = new URL(image, origin).toString();
    document.title = localizedTitle;
    upsertMeta('meta[name="description"]', { name: 'description', content: localizedDescription });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'mapbl' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: localizedTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: localizedDescription });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: localizedTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: localizedDescription });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });

    let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;

    const oldJsonLd = document.head.querySelector('script[data-mapbl-json-ld]');
    oldJsonLd?.remove();
    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.mapblJsonLd = 'true';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [canonicalPath, image, localizedDescription, localizedTitle, robots, structuredData]);
};
